import React from 'react';
import FadeIn from './FadeIn';
import PhotoCarousel from './PhotoCarousel';
import { useImagePreloader } from '../hooks/useImagePreloader';

const ballroomPhotos = [
  "https://ik.imagekit.io/aeu02xvjgn/26-padua-hills-theatre-claremont-persian-wedding-photography.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/22-padua-hills-theatre-claremont-persian-wedding-photography-1600x1066.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/IMG_1364.JPG",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Michelle-and-Joshua-389.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Kelley-and-Faisal-109.jpg",
];

const plazaPhotos = [
  "https://ik.imagekit.io/aeu02xvjgn/09-padua-hills-claremont-wedding-photography.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/0302-CA-Padua-HIlls-Theatre-Wedding-Photography.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/0315-RM-Pauda-Hills-Theatre-Los-Angeles-County-Wedding-Photography.jpg-nggid041168-ngg0dyn-0x1000-00f0w010c010r110f110r010t010.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Carols%20secret%20garden.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/i-Wh5HSCQ-X3.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Danielle-and-Brian-107.jpg",
];

const terracePhotos = [
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Michelle-and-Joshua-922.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Lauren-and-DJ-30.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Michelle-and-Joshua-360.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/i-Ff8RRgF-X3.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Michelle-and-Joshua-1350.jpg",
];

const bridalSuitePhotos = [
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Danielle-and-Brian-42.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Jessica-and-Christian-26.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Lauren-and-DJ-93.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Lauren-and-DJ-91.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Jessica-and-Christian-61.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Wedding-Photos-Madison-and-Daniel-7.jpg",
];

const oliveGrovePhotos = [
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Rica-and-Giacomo-36.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Angela-and-Mark-141.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Erin-and-Mike-25.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Lauren-and-DJ-168.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Angela-and-Mark-52.jpg",
  "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Erin-and-Mike-33.jpg",
];

const allPhotos = [...ballroomPhotos, ...plazaPhotos, ...terracePhotos, ...bridalSuitePhotos, ...oliveGrovePhotos];

const amenityItems = [
  {
    id: "ballroom",
    tag: "The Main Reception Space",
    title: "The Historic Ballroom",
    desc: "Vaulted ceilings. Wrought iron chandeliers. Restored hardwood floors that have held celebrations since 1930. The Ballroom is the heart of the estate — a grand yet intimate space where Spanish Revival architecture meets the warmth of a room full of people you love. With customizable lighting and ample space, it perfectly accommodates wedding receptions, corporate events, and grand family celebrations.",
    capacity: "Up to 250 guests",
    usedFor: "Wedding receptions · Corporate events · Quinceañeras · Bar and bat mitzvahs · Special events",
    photos: ballroomPhotos,
  },
  {
    id: "plaza",
    tag: "Outdoor Ceremony & Dining",
    title: "Plaza Courtyard + Gazebo",
    desc: "Framed by a canopy of century-old olive trees and the iconic theatre facade, the Plaza Courtyard is where most Padua ceremonies begin. At its center stands a one-of-a-kind wrought iron gazebo — a timeless focal point. As the sun sets, the space transforms into a candlelit dining setting with linen-draped tables, offering the perfect indoor-outdoor wedding venue flexibility.",
    capacity: "Ceremony up to 350 seated · Dining up to 350 guests",
    usedFor: "Outdoor wedding ceremonies · Ceremony and reception · Cocktail hour · Customizable events",
    photos: plazaPhotos,
    interval: 4200,
  },
  {
    id: "terrace",
    tag: "Panoramic Views & Golden Hour",
    title: "Sunset-View Terrace",
    desc: "Perched at the edge of the foothills, the Terrace offers something most Los Angeles wedding venues simply cannot — a sweeping, unobstructed view of the valley below. Whether it's an intimate ceremony at dusk, a cocktail hour as the sun drops, or a sunset wedding portrait session, the Terrace delivers a backdrop unlike anywhere else.",
    capacity: "Ceremony up to 250 seated · Dining up to 120 guests",
    usedFor: "Sunset weddings · Outdoor ceremonies · Cocktail hour · Photo shoots",
    photos: terracePhotos,
    interval: 4000,
  },
  {
    id: "suite",
    tag: "Morning-Of Preparation",
    title: "Bridal Suite",
    desc: "Your wedding day begins here. The Bridal Suite is a calm, light-filled space designed to be a true luxury sanctuary — not just a room to get ready in, but a place to breathe, laugh, and take in the morning. With large mirrors positioned for beautiful natural light, it's thoughtfully set up for both relaxation and stunning pre-ceremony photography.",
    capacity: null,
    usedFor: "Getting ready · Bridal suite preparation · Wedding photography",
    photos: bridalSuitePhotos,
    interval: 3600,
  },
  {
    id: "olive-grove",
    tag: "Signature Landscape",
    title: "The Olive Grove",
    desc: "The olive grove is the soul of Padua Hills. A canopy of historic trees creates a naturally filtered light that photographers consistently describe as extraordinary. This is where most couples choose to do their first look. It's where wedding and engagement portraits feel effortless and where the classic grounds of the estate reveal their full character.",
    capacity: null,
    usedFor: "First look · Wedding and engagement photo shoots · Olive grove photos · Ambient event backdrop",
    photos: oliveGrovePhotos,
    interval: 4100,
  },
  {
    id: "bar",
    tag: "Social Transition Space",
    title: "Cocktail Bar & Lobby",
    desc: "Adjacent to the Ballroom and designed for easy flow, the Cocktail Bar and Lobby is where your guests gather between moments. A full bar anchors the space, while the historic lobby creates an atmosphere that feels warm and social. It works beautifully for a relaxed pre-reception cocktail hour or an elevated corporate networking event.",
    capacity: null,
    usedFor: "Cocktail hour · Corporate and conference mixers · Private events",
    img: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80&w=1200",
  },
];

const AmenitySection = ({ item, idx }) => {
  const isEven = idx % 2 === 0;
  return (
    <div id={item.id} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-16 lg:gap-24 items-center amenity-scroll-anchor`}>
      <FadeIn className="w-full lg:w-3/5">
        <div className="aspect-[16/10] overflow-hidden rounded-sm shadow-2xl border-[12px] border-white">
          {item.photos ? (
            <PhotoCarousel photos={item.photos} interval={item.interval || 3800} />
          ) : (
            <img src={item.img} alt={item.title} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
          )}
        </div>
      </FadeIn>
      
      <FadeIn delay={0.2} className="w-full lg:w-2/5">
        <span className="text-brand-olive uppercase tracking-[0.25em] text-[9px] font-bold mb-4 block">{item.tag}</span>
        <h3 className="font-serif text-3xl md:text-5xl text-brand-charcoal mb-8 font-light leading-tight">{item.title}</h3>
        <p className="font-sans text-brand-charcoal/80 leading-loose font-light mb-10 text-base tracking-wide">{item.desc}</p>
        
        {(item.capacity || item.usedFor) && (
          <div className="border-t border-brand-stone/40 pt-8 space-y-6 mb-10">
            {item.capacity && (
              <div>
                <span className="text-[9px] uppercase tracking-[0.25em] text-brand-bronze font-bold block mb-2">Capacity</span>
                <p className="font-sans text-brand-charcoal/80 text-sm font-light">{item.capacity}</p>
              </div>
            )}
            {item.usedFor && (
              <div>
                <span className="text-[9px] uppercase tracking-[0.25em] text-brand-bronze font-bold block mb-2">Commonly Used For</span>
                <p className="font-sans text-brand-charcoal/80 text-sm font-light">{item.usedFor}</p>
              </div>
            )}
          </div>
        )}
        
        <a href="#contact" className="inline-block text-[10px] uppercase tracking-[0.25em] text-brand-charcoal border-b border-brand-charcoal pb-2 hover:text-brand-olive hover:border-brand-olive transition-all font-bold">
          Inquire About This Space
        </a>
      </FadeIn>
    </div>
  );
};

const Amenities = () => {
  useImagePreloader(allPhotos);

  return (
    <section id="amenities" className="bg-brand-vintage py-32 md:py-48" style={{ scrollMarginTop: '120px' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-32 max-w-4xl mx-auto">
          <FadeIn>
            <span className="text-brand-bronze uppercase tracking-[0.4em] text-[10px] font-bold mb-6 block">The Estate Spaces</span>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-charcoal font-light mb-10 leading-[1.1]">
              Every Space, <br className="hidden md:block" />
              <span className="italic">Thoughtfully Designed</span>
            </h2>
            <div className="w-24 h-[1px] bg-brand-stone mx-auto mb-12" />
            <p className="font-sans text-brand-charcoal/80 font-light leading-relaxed text-lg tracking-wide mb-6">
              Padua Hills is a full-service event venue — designed to hold every moment of your celebration with care. From the first quiet hour in the Bridal Suite to the last song on the Ballroom floor, each space has been shaped to feel intentional, beautiful, and entirely yours for weddings, corporate events, or private milestones.
            </p>
          </FadeIn>
        </div>

        <div className="space-y-32 md:space-y-48">
          {amenityItems.map((item, idx) => (
            <AmenitySection key={item.id} item={item} idx={idx} />
          ))}
        </div>

        <div id="vendors" className="mt-48 pt-32 border-t border-brand-stone/30" style={{ scrollMarginTop: '120px' }}>
          <FadeIn className="text-center max-w-4xl mx-auto">
            <span className="text-brand-bronze uppercase tracking-[0.3em] text-[10px] font-bold mb-6 block">Trusted Partners</span>
            <h3 className="font-serif text-4xl md:text-5xl text-brand-charcoal mb-8 font-light">Our Preferred Vendor List</h3>
            <div className="w-16 h-[1px] bg-brand-stone mx-auto mb-10" />
            <p className="font-sans text-brand-charcoal/80 font-light mb-6 text-lg leading-relaxed tracking-wide">
              Over the years, we've built relationships with some of Southern California's finest wedding professionals — wedding photography experts, videographers, florists, emcee and DJ services, event planning specialists, and more — who know this estate intimately and bring out the best in it.
            </p>
            <p className="font-sans text-brand-charcoal/60 font-light mb-16 text-base leading-relaxed tracking-wide">
              Our preferred vendor list is a curated starting point, not a restriction. You retain full flexibility over your vendor choices. Think of it as a shortlist of people we trust — and who already know how to make our indoor and outdoor venue spaces look their absolute best.
            </p>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {['Wedding Photography', 'Videography', 'Floral Design', 'Officiants', 'Live Music', 'Emcee & DJ Services', 'Hair & Makeup', 'Event Planning'].map((service, i) => (
                <div key={i} className="py-8 border border-brand-stone/30 bg-white shadow-sm text-[10px] uppercase tracking-[0.2em] font-bold text-brand-charcoal/70 hover:border-brand-olive transition-colors cursor-default">
                  {service}
                </div>
              ))}
            </div>
            
            <a href="#contact" className="inline-block border border-brand-charcoal text-brand-charcoal px-12 py-4.5 text-[10px] uppercase tracking-[0.25em] hover:bg-brand-charcoal hover:text-white transition-all font-bold">
              Check Availability
            </a>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default Amenities;